import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, ChevronRight } from 'lucide-react';
import { useMyEvents } from "@/hooks/useMyEvents";
import { MyEventCardHorizontal } from "./MyEventCardHorizontal";
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

interface MyEventsProps {
  userId?: string;
  isOwnProfile?: boolean;
}

const MyEvents = ({ userId, isOwnProfile = true }: MyEventsProps) => {
  const { myEvents, loading } = useMyEvents(userId);
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const sectionTitle = isOwnProfile ? t('profile.myEvents') : t('profile.eventHistory');
  const emptyTitle = isOwnProfile ? t('profile.noEvents') : t('profile.noEventsRegistered');
  const emptyMessage = isOwnProfile 
    ? t('profile.registerFirst')
    : t('profile.noEventsOther');

  if (loading) {
    return (
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Trophy className="h-5 w-5 text-primary" />
            <span>{t('profile.myEvents')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-36 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const allEvents = [
    ...myEvents.active,
    ...myEvents.upcoming,
    ...myEvents.finished
  ];
  
  const totalEvents = allEvents.length;

  if (!isOwnProfile) {
    const otherProfileEvents = allEvents.slice(0, 5);
    return (
      <Card className="shadow-md border-0">
        <CardHeader className="px-4 py-3">
          <CardTitle className="flex items-center justify-between text-base">
            <div className="flex items-center space-x-2">
              <Trophy className="h-4 w-4 text-primary" />
              <span>{sectionTitle}</span>
            </div>
            <Badge variant="secondary" className="text-[10px]">{totalEvents} {t('profile.total')}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 py-2">
          {otherProfileEvents.length === 0 ? (
            <div className="text-center py-6">
              <Trophy className="h-10 w-10 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-base font-semibold mb-2">{emptyTitle}</h3>
              <p className="text-sm text-muted-foreground">{emptyMessage}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {otherProfileEvents.map((event) => (
                <MyEventCardHorizontal key={event.registrationId || event.id} event={event} isOwnProfile={false} />
              ))}
              {totalEvents > 5 && (
                <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
                  {t('profile.viewAllEvents')} ({totalEvents})
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const displayEvents = allEvents.slice(0, 3);

  return (
    <Card className="shadow-lg border-0">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Trophy className="h-5 w-5 text-primary" />
            <span>{sectionTitle}</span>
          </div>
          <Badge variant="secondary">{totalEvents} {t('profile.total')}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {displayEvents.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">{emptyTitle}</h3>
            <p className="text-sm text-muted-foreground mb-4">{emptyMessage}</p>
            <Button onClick={() => navigate('/')}>{t('profile.exploreEvents')}</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {displayEvents.map((event) => (
              <MyEventCardHorizontal key={event.registrationId || event.id} event={event} />
            ))}
            {totalEvents > 0 && (
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => navigate('/my-events')}
              >
                {t('common.viewAll')} ({totalEvents})
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyEvents;
